import { defineMessages, useIntl } from 'react-intl';
import {
    FormLabel,
    Modal, NumberInput, RadioButton, Section,
    TextArea,
} from '@carbon/react';
import { useEffect, useId, useState } from 'react';

import styles from './PointsHandler.module.scss';

import sharedMessages from 'Components/utils/sharedMessages';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';

type PointsOperationType = 'added' | 'removed';

type Props = {
    label: string;
    onSubmit: (reason: string, numberOfPointsToChange: number) => Promise<void>;
    isLoading?: boolean;
    onClose: () => void;
    open: boolean;
    initialReason?: string;
    initialPoints?: number;
    initialOperationType?: PointsOperationType;
}

const PointsHandler = ({
    label,
    onSubmit,
    isLoading,
    onClose,
    open,
    initialPoints = 0,
    initialReason = '',
    initialOperationType = 'added',
}: Props) => {
    const intl = useIntl();
    const generatedId = useId();
    const uniqueId = generatedId.replace(/:/g, '');

    const [numberOfPointsToChange, setNumberOfPointsToChange] = useState(Math.abs(initialPoints));
    const [operationType, setOperationType] = useState<PointsOperationType>(initialOperationType);
    const [reason, setReason] = useState(initialReason);
    const currentEntityId = useGetCurrentEntityId();

    useEffect(() => {
        if (!open) {
            return;
        }

        setNumberOfPointsToChange(Math.abs(initialPoints));
        setOperationType(initialOperationType);
        setReason(initialReason);
    }, [initialOperationType, initialPoints, initialReason, open]);

    const normalizedPointsToChange = Math.abs(Number(numberOfPointsToChange) || 0);
    const isDisabled = normalizedPointsToChange === 0;

    const handleFormSubmit = async () => {
        if (!currentEntityId) {
            return;
        }

        const points = operationType === 'added' ? normalizedPointsToChange : -normalizedPointsToChange;

        try {
            await onSubmit(reason, points);
            onClose();
        } catch (e: any) {
            console.log(e); // TO_DO
        }
    };

    const changeNumberOfPoints = (value: number | string) => {
        setNumberOfPointsToChange(Math.abs(Number(value) || 0));
    };

    const onRadioChange = (value: PointsOperationType) => {
        setOperationType(value);
    };

    return (
        <Section level={2}>
            <Modal
                open={open}
                onRequestClose={onClose}
                onRequestSubmit={handleFormSubmit}
                primaryButtonText={intl.formatMessage(sharedMessages.save)}
                loadingStatus={isLoading ? 'active' : undefined}
                primaryButtonDisabled={isLoading || isDisabled}
                secondaryButtonText={intl.formatMessage(sharedMessages.close)}
                modalHeading={label}
                closeButtonLabel={intl.formatMessage(sharedMessages.close)}
            >
                <div className={styles.container}>
                    <div>
                        <FormLabel>{intl.formatMessage(messages.setRemovalOrAddition)}</FormLabel>
                        <div className={styles.radioContainer}>
                            <RadioButton
                                id={`added-${uniqueId}`}
                                labelText={intl.formatMessage(messages.addition)}
                                onChange={() => { onRadioChange('added'); }}
                                value="added"
                                checked={operationType === 'added'}
                            />
                            <RadioButton
                                id={`removed-${uniqueId}`}
                                labelText={intl.formatMessage(messages.removed)}
                                onChange={() => { onRadioChange('removed'); }}
                                value="removed"
                                checked={operationType === 'removed'}
                            />
                        </div>
                    </div>
                    <NumberInput
                        id={`add-or-removal-points-${uniqueId}`}
                        label={intl.formatMessage(messages.setHowManyPoints)}
                        type="number"
                        min={0}
                        onChange={(_, { value }) => { changeNumberOfPoints(value); }}
                        value={numberOfPointsToChange}
                        required
                        title={intl.formatMessage(messages.setHowManyPoints)}
                    />
                    <TextArea
                        enableCounter
                        id={`add-ore-remove-points-reason-${uniqueId}`}
                        value={reason}
                        onChange={(event) => { setReason(event.target.value); }}
                        labelText={intl.formatMessage(messages.setReason)}
                        maxCount={500}
                        rows={4}
                    />
                </div>
            </Modal>
        </Section>
    );
};

const messages = defineMessages({
    setRemovalOrAddition: {
        id: 'src.Screens.Dashboard.Team.Components.TeamCellHandler.TeamInteractions.Interactions.PointsHandler.PointsHandler.setRemovalOrAddition',
        defaultMessage: 'Ustaw, czy punkty mają być dodane, czy usunięte',
    },
    addition: {
        id: 'src.Screens.Dashboard.Team.Components.TeamCellHandler.TeamInteractions.Interactions.PointsHandler.PointsHandler.addition',
        defaultMessage: 'Dodane',
    },
    removed: {
        id: 'src.Screens.Dashboard.Team.Components.TeamCellHandler.TeamInteractions.Interactions.PointsHandler.PointsHandler.removed',
        defaultMessage: 'Usunięte',
    },
    setHowManyPoints: {
        id: 'src.Screens.Dashboard.Team.Components.TeamCellHandler.TeamInteractions.Interactions.PointsHandler.PointsHandler.setHowManyPoints',
        defaultMessage: 'Ustaw ile punktów',
    },
    setReason: {
        id: 'src.Screens.Dashboard.Team.Components.TeamCellHandler.TeamInteractions.Interactions.PointsHandler.PointsHandler.setReason',
        defaultMessage: 'Ustaw powód zmiany',
    },
});

export default PointsHandler;
